/**
 * Wave I — analysis & visualization commands (zebra/curvature/draft/dir/
 * showEdges/envMap + live section plane). These toggle display overlays via
 * the shared view store; the actual overlay rendering lives in
 * SystemOverlayLayer / an analysis branch reading useViewStore.analysisMode.
 * (command-parity-plan.md)
 */

import { runInteraction, PT, requireSelection, solidMesh } from './lib';
import { useViewStore, type AnalysisMode } from '../../../state/commandAuxStores';
import { useAppStore } from '../../../state/store';
import type { RunnerMap } from '../runnerRegistry';
import { notify } from '../../../utils/notify';
import { vertexNormals } from '../meshFactory';
import i18n from '../../../i18n';

function setMode(mode: AnalysisMode, label: string): string {
    const s = useViewStore.getState();
    const next: AnalysisMode = s.analysisMode === mode ? 'none' : mode;
    s.set('analysisMode', next);
    notify(i18n.t('tools.notify.analysisOverlay', { state: next === 'none' ? i18n.t('tools.notify.stateOff') : label }), 'info');
    return `Analysis "${next === 'none' ? offLabel(label) : label}" ${next === 'none' ? 'disabled' : 'enabled'}.`;
}
const offLabel = (l: string) => l;

export const analysisRunners: RunnerMap = {
    'ana.showEdges': () => setMode('edges', 'Naked Edges'),
    'ana.dir': () => setMode('normals', 'Surface Direction'),
    'ana.zebra': () => setMode('zebra', 'Zebra Stripes'),
    'ana.draft': () => setMode('draft', 'Draft Angle'),
    'ana.curvature': () => setMode('curvature', 'Curvature Map'),
    'ana.envMap': () => setMode('envmap', 'Environment Map'),

    'ana.curvatureGraph': () => {
        const list = requireSelection('CURVATUREGRAPH');
        if (!list) return 'CURVATUREGRAPH: select a curve first.';
        const e = list[0];
        const pts = (e.data.controlPoints as number[][]) ?? (e.data.vertices as number[][]);
        if (!pts || pts.length < 3) return 'CURVATUREGRAPH: pick a curve with 3+ points.';
        // Curvature per interior vertex (Menger-lite via angle).
        let maxK = 0;
        const kappas: number[] = [];
        for (let i = 1; i < pts.length - 1; i++) {
            const a = pts[i - 1], b = pts[i], c = pts[i + 1];
            const v1 = [b[0] - a[0], b[1] - a[1]], v2 = [c[0] - b[0], c[1] - b[1]];
            const cross = v1[0] * v2[1] - v1[1] * v2[0];
            const l1 = Math.hypot(...v1) || 1, l2 = Math.hypot(...v2) || 1;
            const k = 2 * Math.abs(cross) / (l1 * l2 * (l1 + l2));
            kappas.push(k); maxK = Math.max(maxK, k);
        }
        // Publish for the AnalysisOverlay graph render (points = the interior
        // vertices the kappas belong to, with their world Z).
        useViewStore.getState().set('curvatureGraph', {
            points: pts.slice(1, -1).map((p) => [p[0], p[1], p[2] ?? 0] as [number, number, number]),
            kappas,
        });
        notify(i18n.t('tools.notify.curvatureGraph', { max: maxK.toFixed(3), count: kappas.length }), 'info');
        return `CURVATUREGRAPH: graph computed (peak curvature ${maxK.toFixed(4)}).`;
    },

    'sol.sectionPlane': () => {
        runInteraction({
            steps: [PT('SECTIONPLANE: corner 1'), PT('SECTIONPLANE: corner 2')],
            complete: (pts) => {
                const s = useViewStore.getState();
                const active = !!s.section;
                s.set('section', active ? null : {
                    a: [pts[0].x, pts[0].y], b: [pts[1].x, pts[1].y],
                });
                useAppStore.getState().updateView({ viewMode: 'xray' });
                notify(active ? 'Section plane removed.' : 'Live section plane enabled.', 'success');
                return active ? 'Section plane turned off.' : 'Section plane placed (drag in view; re-run to remove).';
            },
        });
        return 'SECTIONPLANE: draw a vertical plane';
    },

    // Quick statistics for the picked surface — real math via vertexNormals.
    'ana.stats': () => {
        const list = requireSelection('ANALYZESURFACE');
        const solid = list?.find((e) => e.type === 'solid');
        const m = solid ? solidMesh(solid) : null;
        if (!m || m.vertices.length === 0) return 'Analysis: pick a surface/solid.';
        const n = vertexNormals(m);
        const avgZ = n.reduce((acc, v) => acc + v[2], 0) / n.length;
        return `Surface stats: ${m.vertices.length} points, ${m.indices.length} faces, mean normal Z ${avgZ.toFixed(3)}.`;
    },
};
